// brandWheel.js — THE canonical iYuga Wheel renderer for the web app.
// WL-001 (Fable Hail Mary). Ported verbatim (geometry) from the design law
// source: C:\iYuga\design-handoff\harness\elevations\brand-wheel.mjs
// (Gordon design law, notes6 2026-07-03: "MAKE ALL WHEELS ACROSS ALL DESIGNS
// ON BRAND SO I STOP HAVING TO SAY THIS").
//
// Design-conformance gate G-19 HARD-BLOCKS hand-rolled Wheels (a segmented
// ring/donut with >=4 arc <path>s, or a conic-gradient wheel) unless it
// references `brandWheelSVG` / `brand-wheel` / `BRAND_WHEEL`. Any decorative /
// placeholder Wheel in the web app MUST come from here — no equal-quarter pies,
// no crosshairs, no invented palettes.
//
// Brand geometry (verified in the deck; decisions-log ratio row #23):
//   · Portrait/inner radius = 0.75 x outer radius (the app's Portrait ratio)
//   · Segment gaps are PARALLEL-EDGED (constant perpendicular width base->rim)
//   · 4-7 Segments of UNEQUAL spans (a real Wheel is never four perfect quarters)
//   · Line-art variant available for icons/pickers (stroke, no fill)
//
// This module is framework-free (pure string output) so it can be used at build
// time, in tests, or inside the React <BrandWheel /> component (index.jsx).

export const BRAND_WHEEL_COLORS = [
  "#9B5AF8",
  "#6D5EF6",
  "#34D399",
  "#F59E0B",
  "#F472B6",
  "#22D3EE",
  "#A78BFA",
];

// Default representative Wheel: 6 unequal segments (same set the deck loader holds on).
export const BRAND_WHEEL_SPANS = [66, 50, 72, 44, 62, 66];
export const BRAND_WHEEL_PALETTE = ["#9B5AF8", "#34D399", "#F59E0B", "#F472B6", "#22D3EE", "#6D5EF6"];

// The fixed wheel centre/outer-radius — shared by every wedge geometry call so
// the canonical Wheel and any partial-sweep (BrandWheelLoader) draw of it can
// never drift apart. Exported so a caller that needs the wedge ring to reach
// its OWN container's edge (no built-in breathing room) can compute the scale
// that does that — see WHEEL_EDGE_FILL_SCALE below.
export const WHEEL_C = 60;
export const WHEEL_R = 56;
export const WEDGE_RATIO = 0.75; // inner/outer radius — the app's Portrait ratio; do not change
export const WEDGE_GAP = 4; // full gap width in viewBox units (constant base->periphery)

// gordon-0917-collection-default-cover-wedge-rim-gap — BrandWheel's wedge ring
// intentionally stops short of its own viewBox edge (WHEEL_R=56 of a
// WHEEL_C=60 half-width, ~6.7% margin) everywhere it's used as a decorative
// mark (loaders, favicons, marketing). A caller that instead wants the ring to
// reach its container's edge EXACTLY — e.g. a collection's default cover,
// which must match the real-cover D3 wheel's edge-to-edge fill (margin 0, see
// CollectionCard.jsx's wheelArtProps) — scales the whole SVG by this factor so
// the rendered outer radius lands on the viewBox edge instead of short of it.
export const WHEEL_EDGE_FILL_SCALE = WHEEL_C / WHEEL_R;

/**
 * The `d` for a single wedge spanning `a0Deg` -> `a1Deg` (parallel-edged gap,
 * same radii/dividers/rim as the canonical Wheel). `a1Deg` need not be the
 * segment's full end angle — BrandWheelLoader's arc-sweep passes a growing
 * value here so a segment's arc can GROW CLOCKWISE from its start angle up to
 * its own end angle (the same shape createPie.js's D3 arcTween produces, see
 * arcSweepEngine.js) while tracing the exact geometry this function also uses
 * for the fully-drawn wheel below.
 * @returns {string}
 */
export function wedgePathD(a0Deg, a1Deg, opts = {}) {
  const { ratio = WEDGE_RATIO, gap = WEDGE_GAP } = opts;
  const R = WHEEL_R;
  const RIN = R * ratio;
  const HG = gap / 2;
  const AOUT = Math.asin(HG / R);
  const AIN = Math.asin(HG / RIN);
  const pt = (th, r) =>
    (WHEEL_C + r * Math.sin(th)).toFixed(3) + " " + (WHEEL_C - r * Math.cos(th)).toFixed(3);

  const b0 = (a0Deg * Math.PI) / 180;
  const b1 = (a1Deg * Math.PI) / 180;
  const laf = b1 - b0 > Math.PI ? 1 : 0;
  return (
    "M" +
    pt(b0 + AOUT, R) +
    " A" +
    R +
    " " +
    R +
    " 0 " +
    laf +
    " 1 " +
    pt(b1 - AOUT, R) +
    " L" +
    pt(b1 - AIN, RIN) +
    " A" +
    RIN +
    " " +
    RIN +
    " 0 " +
    laf +
    " 0 " +
    pt(b0 + AIN, RIN) +
    " Z"
  );
}

/**
 * Build the inner SVG markup (paths + optional white Portrait disc) for a
 * brand Wheel. Returned WITHOUT the outer <svg> wrapper so React can render it
 * via dangerouslySetInnerHTML and the string helper can wrap it itself.
 * @returns {string}
 */
export function brandWheelInner(opts = {}) {
  const {
    spans = BRAND_WHEEL_SPANS,
    colors = BRAND_WHEEL_PALETTE,
    ratio = WEDGE_RATIO,
    gap = WEDGE_GAP,
    lineArt = false, // stroke-only variant for icons/pickers
    stroke = "#123051", // line-art ink (brand navy)
    strokeWidth = 5, // line-art stroke in viewBox units (120 box)
    disc = true, // white Portrait disc under the center
    portrait = "", // extra SVG injected at the center (logo/avatar/image), optional
  } = opts;

  const RIN = WHEEL_R * ratio;

  let paths = "";
  let a0 = 0;
  for (let i = 0; i < spans.length; i++) {
    const d = wedgePathD(a0, a0 + spans[i], { ratio, gap });
    paths += lineArt
      ? `<path d="${d}" fill="none" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-linejoin="round"/>`
      : `<path d="${d}" fill="${colors[i % colors.length]}"/>`;
    a0 += spans[i];
  }

  const discSvg =
    !lineArt && disc
      ? `<circle cx="${WHEEL_C}" cy="${WHEEL_C}" r="${(RIN - 1.5).toFixed(1)}" fill="#fff"/>`
      : "";

  return `${paths}${discSvg}${portrait}`;
}

/**
 * Full standalone SVG string for a brand Wheel (build-time / non-React use).
 * @returns {string}
 */
export function brandWheelSVG(opts = {}) {
  const { size = 96, ariaLabel = "" } = opts;
  const inner = brandWheelInner(opts);
  const aria = ariaLabel ? `role="img" aria-label="${ariaLabel}"` : `aria-hidden="true"`;
  return (
    `<svg ${aria} width="${size}" height="${size}" viewBox="0 0 120 120" ` +
    `style="display:block;flex:0 0 auto">${inner}</svg>`
  );
}

// Fisher-Yates — used so a reroll's color draw doesn't repeat brand hues in the
// same order every cycle (a stable `slice(0, n)` would always start "purple first").
function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * `count` unequal segment spans (degrees) that sum to exactly 360 — "a real
 * Wheel is never four perfect quarters" (brand geometry law above). Weighted
 * random draw, not an even split, so consecutive spans differ visibly.
 * @param {number} count
 * @returns {number[]}
 */
export function randomSpans(count) {
  const weights = Array.from({ length: count }, () => 0.5 + Math.random());
  const total = weights.reduce((sum, w) => sum + w, 0);
  const spans = weights.map((w) => Math.round((w / total) * 360));
  // Rounding can drift the sum off 360 by a degree or two; put the remainder on
  // the last segment so the wedges always close the circle exactly.
  spans[spans.length - 1] += 360 - spans.reduce((sum, s) => sum + s, 0);
  return spans;
}

// Parse "#RRGGBB" -> [r,g,b]. Tolerates a leading '#'. Non-hex returns null so
// callers can skip mixing gracefully.
function hexToRgb(hex) {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(String(hex).trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

// [r,g,b] -> "#RRGGBB" (upper-case, matching the palette literals).
function rgbToHex([r, g, b]) {
  const h = (v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0");
  return ("#" + h(r) + h(g) + h(b)).toUpperCase();
}

// Linear midpoint mix of two hex colours at `t` (0 => a, 1 => b). Used ONLY to
// synthesise extra DISTINCT colours when a palette has fewer colours than the
// wheel has segments (see seamSafeSegmentColors' interpolation branch).
export function mixHex(a, b, t = 0.5) {
  const ra = hexToRgb(a);
  const rb = hexToRgb(b);
  if (!ra || !rb) return a;
  return rgbToHex([
    ra[0] + (rb[0] - ra[0]) * t,
    ra[1] + (rb[1] - ra[1]) * t,
    ra[2] + (rb[2] - ra[2]) * t,
  ]);
}

/**
 * QA-1257 (Gordon design fix): the wheel is CIRCULAR, so segment[0] and
 * segment[n-1] are ADJACENT — the loader used to colour segment `i` as
 * `palette[i % palette.length]`, which for a 5-colour palette on a 6-segment
 * wheel makes segment 5 === `palette[5 % 5]` === segment 0: the ring visibly
 * BEGINS and ENDS on the same colour at the midnight seam (the reported bug).
 *
 * This returns exactly `count` colours for a circular wheel that are SEAM-SAFE:
 *   · no two ADJACENT segments share a colour, INCLUDING the [0] / [count-1] seam,
 *   · and, whenever there is enough colour to do so, EVERY segment is a distinct
 *     colour.
 * When the palette already has >= `count` distinct colours (the loader's own
 * case — every curated palette carries >= LOADER_SEGMENT_COUNT), it simply takes
 * the first `count` DISTINCT palette colours in order: all different, so adjacency
 * and the seam are safe by construction and NO off-palette colour is ever
 * introduced (keeps every fill inside the curated set). When the palette has
 * fewer distinct colours than segments, it synthesises additional distinct
 * colours by midpoint-mixing consecutive palette hues until there are `count` of
 * them — so uniqueness (and therefore seam-safety) still holds.
 *
 * @param {string[]} palette ordered candidate colours
 * @param {number} count number of segments to colour
 * @returns {string[]} exactly `count` seam-safe colours
 */
export function seamSafeSegmentColors(palette, count) {
  if (!Array.isArray(palette) || palette.length === 0 || count <= 0) return [];
  // Distinct palette colours, order preserved (the curated warm/cool adjacency).
  const distinct = [];
  const seen = new Set();
  for (const c of palette) {
    if (!seen.has(c)) {
      seen.add(c);
      distinct.push(c);
    }
  }

  // Enough colour to give every segment its own: a distinct selection is
  // trivially seam-safe (nothing equals its neighbour, nor the wrap-around).
  if (distinct.length >= count) return distinct.slice(0, count);

  // Fewer distinct colours than segments: grow the pool with synthesised
  // midpoints of consecutive hues until we have `count` DISTINCT colours. All
  // distinct => still seam-safe (no adjacent — or seam — repeat).
  const pool = distinct.slice();
  const inPool = new Set(pool);
  let i = 0;
  // Bounded: each step adds one colour; guard against a pathological all-equal
  // palette (already deduped, so distinct.length >= 1) by nudging t if a mix
  // collides.
  let guard = 0;
  while (pool.length < count && guard < count * 8) {
    guard += 1;
    const a = distinct[i % distinct.length];
    const b = distinct[(i + 1) % distinct.length];
    let t = 0.5;
    let mixed = mixHex(a, b, t);
    let bump = 0;
    while (inPool.has(mixed) && bump < 6) {
      t = 0.5 + (bump + 1) * 0.07;
      mixed = mixHex(a, b, t);
      bump += 1;
    }
    if (!inPool.has(mixed)) {
      inPool.add(mixed);
      pool.push(mixed);
    }
    i += 1;
  }
  return pool.slice(0, count);
}

/**
 * A fresh, randomized on-brand Wheel: 4-7 unequal segments (BRAND_WHEEL_SPANS'
 * own range) coloured from a shuffled draw of the brand palette. This is what
 * BrandWheelLoader re-rolls on its loop so the loader visibly "generates
 * randomized segment sizes and colors as it animates" (Gordon, bug #908)
 * instead of pulsing the opacity of one static wheel.
 * @returns {{spans: number[], colors: string[]}}
 */
export function randomBrandWheel(opts = {}) {
  const { minSegments = 4, maxSegments = 7, palette } = opts;
  const count =
    minSegments + Math.floor(Math.random() * (maxSegments - minSegments + 1));
  // A caller-supplied `palette` (the BrandWheelLoader's #937 per-cycle pick) is
  // a CURATED arrangement. QA-1257: it is now run through seamSafeSegmentColors
  // so the returned `colors` are exactly `count` long AND seam-safe on the
  // circular wheel — segment[0] can never equal segment[count-1] (the old
  // `i % palette.length` wrap collision). With no palette, the historical
  // shuffled brand-hue draw is kept unchanged (back-compat for every other
  // caller); that draw is already distinct so it is seam-safe too.
  const colors = palette
    ? seamSafeSegmentColors(palette, count)
    : shuffle(BRAND_WHEEL_COLORS).slice(0, count);
  return { spans: randomSpans(count), colors };
}

export default brandWheelSVG;
